/* ═══════════════════════════════════════════════════════════════════════════
   GFI Backup / Restore — full-app data snapshot
   Snapshots every gfi-* localStorage key + the IndexedDB "documents"
   store (PDFs, photos, attachments) into a single JSON file the user
   can download and re-import. Also installs a setItem patch that
   counts business writes so the App shell can prompt for a reminder
   backup every N saves or after 24 h.
   ═══════════════════════════════════════════════════════════════════════════ */

import { saveDoc } from "./gfiDB";

/* Keys that should be backed up but NOT counted as business saves.
   UI prefs and the backup bookkeeping itself flip constantly and
   would spam the "you owe a backup" prompt. */
const META_KEYS = new Set([
  "gfi-last-backup",
  "gfi-saves-since-backup",
  "gfi-backup-reminder-dismissed",
  "gfi-module",
  "gfi-lang",
  "gfi-theme",
  "gfi-sections",
]);

/* Raised from 15 → 200 per user feedback: a single editing session
   easily clears 15 saves, which made the banner nag continuously.
   200 lines up better with the "end of day" backup cadence. */
export const SAVES_PER_REMINDER = 200;
export const HOURS_PER_REMINDER = 24;

const IDB_NAME = "gfi-nexus-db";
const IDB_STORE = "documents";

/* ── localStorage snapshot ── */
export function snapshotLocalStorage() {
  var out = {};
  for (var i = 0; i < localStorage.length; i++) {
    var key = localStorage.key(i);
    if (!key || key.indexOf("gfi-") !== 0) continue;
    var raw = localStorage.getItem(key);
    try { out[key] = JSON.parse(raw); } catch (e) { out[key] = raw; }
  }
  return out;
}

/* ── IndexedDB snapshot ── reads the full "documents" store as
   { key: value } pairs. Returns {} on any error so the export still
   succeeds with just the structured data. */
function snapshotIndexedDB() {
  return new Promise(function (resolve) {
    var req;
    try { req = indexedDB.open(IDB_NAME, 1); } catch (e) { resolve({}); return; }
    req.onerror = function () { resolve({}); };
    req.onsuccess = function (e) {
      var db = e.target.result;
      try {
        var tx = db.transaction(IDB_STORE, "readonly");
        var store = tx.objectStore(IDB_STORE);
        var keysReq = store.getAllKeys();
        var valsReq = store.getAll();
        var keys, vals;
        var maybeDone = function () {
          if (!keys || !vals) return;
          var map = {};
          for (var i = 0; i < keys.length; i++) map[keys[i]] = vals[i];
          resolve(map);
        };
        keysReq.onsuccess = function () { keys = keysReq.result || []; maybeDone(); };
        valsReq.onsuccess = function () { vals = valsReq.result || []; maybeDone(); };
        keysReq.onerror = valsReq.onerror = function () { resolve({}); };
      } catch (err) { resolve({}); }
    };
  });
}

/* Restore every key/value into the IndexedDB documents store. Done
   sequentially to keep memory pressure low for large blobs. */
async function restoreIndexedDB(map) {
  if (!map || typeof map !== "object") return;
  var keys = Object.keys(map);
  for (var i = 0; i < keys.length; i++) {
    try { await saveDoc(keys[i], map[keys[i]]); }
    catch (e) { console.error("[gfiBackup] restore doc failed", keys[i], e); }
  }
}

/* ── Public API ── */
export async function buildBackup(opts) {
  opts = opts || {};
  var includeDocs = opts.includeDocs !== false;
  var ls = snapshotLocalStorage();
  var docs = {};
  if (includeDocs) {
    try { docs = await snapshotIndexedDB(); } catch (e) { docs = {}; }
  }
  return {
    version: 1,
    appName: "GFI Nexus",
    exportedAt: new Date().toISOString(),
    counts: {
      localStorageKeys: Object.keys(ls).length,
      indexedDBKeys: Object.keys(docs).length,
    },
    localStorage: ls,
    indexedDB: docs,
  };
}

function ts() {
  var d = new Date();
  var p = function (n) { return String(n).padStart(2, "0"); };
  return d.getFullYear() + "-" + p(d.getMonth() + 1) + "-" + p(d.getDate())
       + "_" + p(d.getHours()) + p(d.getMinutes());
}

export function triggerDownload(backup) {
  var json = JSON.stringify(backup);
  var blob = new Blob([json], { type: "application/json" });
  var url = URL.createObjectURL(blob);
  var a = document.createElement("a");
  a.href = url;
  a.download = "gfi-backup-" + ts() + ".json";
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  setTimeout(function () { URL.revokeObjectURL(url); }, 1000);
  markBackupDone();
}

export async function exportAndDownload(opts) {
  var b = await buildBackup(opts);
  triggerDownload(b);
  return b;
}

export function markBackupDone() {
  try {
    /* Bypass the patched setItem so this write itself doesn't bump
       the counter. The patch stores the original on the function. */
    var orig = localStorage.setItem.__gfiOrig || localStorage.setItem.bind(localStorage);
    orig("gfi-last-backup", new Date().toISOString());
    orig("gfi-saves-since-backup", "0");
    orig("gfi-backup-reminder-dismissed", "");
  } catch (e) {}
}

export async function restoreFromBackup(backup, opts) {
  opts = opts || {};
  if (!backup || typeof backup !== "object" || !backup.localStorage) {
    throw new Error("Invalid backup file — missing localStorage payload");
  }
  /* Bypass the save-counter patch during restore so importing a
     full snapshot doesn't flood gfi-saves-since-backup and trigger
     the reminder banner the moment the user lands back in the app. */
  var orig = localStorage.setItem.__gfiOrig || localStorage.setItem.bind(localStorage);
  if (opts.wipeFirst) {
    var purge = [];
    for (var i = 0; i < localStorage.length; i++) {
      var k = localStorage.key(i);
      if (k && k.indexOf("gfi-") === 0) purge.push(k);
    }
    purge.forEach(function (k) { try { localStorage.removeItem(k); } catch (e) {} });
  }
  Object.keys(backup.localStorage).forEach(function (k) {
    var v = backup.localStorage[k];
    try {
      orig(k, typeof v === "string" ? v : JSON.stringify(v));
    } catch (e) {
      console.error("[gfiBackup] restore key failed", k, e);
    }
  });
  if (backup.indexedDB) await restoreIndexedDB(backup.indexedDB);
  /* Post-restore the on-disk snapshot IS the source of truth, so
     reset the save counter and stamp lastBackup with the backup's
     own exportedAt (or now, as fallback). */
  try {
    orig("gfi-saves-since-backup", "0");
    orig("gfi-last-backup", (backup && backup.exportedAt) || new Date().toISOString());
  } catch (e) {}
}

/* ── setItem patch ── installs once at app boot; subsequent
   business writes increment gfi-saves-since-backup. Meta keys
   (and writes routed through markBackupDone) are skipped. */
export function installSaveCounter() {
  if (typeof window === "undefined") return;
  if (localStorage.setItem.__gfiPatched) return;
  var orig = localStorage.setItem.bind(localStorage);
  var patched = function (key, value) {
    orig(key, value);
    if (typeof key === "string" && key.indexOf("gfi-") === 0 && !META_KEYS.has(key)) {
      try {
        var cur = parseInt(localStorage.getItem("gfi-saves-since-backup") || "0", 10) || 0;
        orig("gfi-saves-since-backup", String(cur + 1));
      } catch (e) {}
    }
  };
  patched.__gfiPatched = true;
  patched.__gfiOrig = orig;
  localStorage.setItem = patched;
}

export function getBackupStatus() {
  var lastIso = "";
  try { lastIso = localStorage.getItem("gfi-last-backup") || ""; } catch (e) {}
  var saves = 0;
  try { saves = parseInt(localStorage.getItem("gfi-saves-since-backup") || "0", 10) || 0; } catch (e) {}
  var hoursSince = Infinity;
  if (lastIso) {
    var ms = Date.now() - new Date(lastIso).getTime();
    if (!isNaN(ms) && ms >= 0) hoursSince = ms / 3600000;
  }
  var dismissedIso = "";
  try { dismissedIso = localStorage.getItem("gfi-backup-reminder-dismissed") || ""; } catch (e) {}
  var dismissedRecently = false;
  if (dismissedIso) {
    var dms = Date.now() - new Date(dismissedIso).getTime();
    /* Dismissals (and auto-shows — see shouldPromptForBackup
       writing the same key) suppress the banner for 24 h. Goal per
       user feedback: never nag more than once a day. */
    if (!isNaN(dms) && dms >= 0 && dms < 24 * 3600000) dismissedRecently = true;
  }
  return { lastIso: lastIso, saves: saves, hoursSince: hoursSince, dismissedRecently: dismissedRecently };
}

export function shouldPromptForBackup() {
  var st = getBackupStatus();
  if (st.dismissedRecently) return false;
  var shouldShow = false;
  if (st.saves >= SAVES_PER_REMINDER) shouldShow = true;
  else if (st.hoursSince >= HOURS_PER_REMINDER && st.saves > 0) shouldShow = true;
  if (shouldShow) {
    /* Treat "shown" the same as "dismissed" for cooldown purposes —
       writing the dismissal stamp now means the banner won't reappear
       for 24 h whether or not the user clicks the X. Matches the
       user's "max once per 24h" requirement. */
    try {
      var orig = localStorage.setItem.__gfiOrig || localStorage.setItem.bind(localStorage);
      orig("gfi-backup-reminder-dismissed", new Date().toISOString());
    } catch (e) {}
  }
  return shouldShow;
}

export function dismissBackupReminder() {
  try {
    var orig = localStorage.setItem.__gfiOrig || localStorage.setItem.bind(localStorage);
    orig("gfi-backup-reminder-dismissed", new Date().toISOString());
  } catch (e) {}
}
